import React, { useState } from 'react';
import axios, { AxiosError } from 'axios';
import { getAuth, sendPasswordResetEmail, signInWithEmailAndPassword } from 'firebase/auth';
import { TextField, Button, Backdrop } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { BarLoader } from 'react-spinners';
import { AppStore } from '../../../../AppStore';
import { ServerErrors } from '../../../../utils/types';
import { loadAllPuzzleStates } from '../../../../utils/persistenceUtils';
import { useNavigate } from 'react-router-dom';
import './SignInDialog.less';

const auth = getAuth();

const SignInDialog = () => {
  const navigate = useNavigate();
  const { isOpen, redirectPathAfterLogin } = AppStore.useState(s => s.signInDialog);

  const [creatingAccount, setCreatingAccount] = useState(true);
  const [resettingPassword, setResettingPassword] = useState(false);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');


  // whitespace for no-error default value so that it still takes up space in doc.
  // just an easy way of accomplishing that
  const [emailError, setEmailError] = useState<string>(' ');
  const [passwordError, setPasswordError] = useState<string>(' ');

  const [loading, setLoading] = useState(false);

  const getMainButtonText = () => {
    if (resettingPassword) {
      return 'Reset Password';
    } else return creatingAccount ? 'Sign Up' : 'Log In';
  };

  const closeDialog = () => {
    if (redirectPathAfterLogin) {
      // delay lets app update so we hit logged-in state so /account doesn't redirect home again after log-in,
      // if that's the page that the user was trying to reach when they were initially directed to login.
      setTimeout(() => {
        navigate(redirectPathAfterLogin);
      }, 350);
    }

    AppStore.update(s => {
      s.signInDialog.isOpen = false;
      s.signInDialog.redirectPathAfterLogin = null;
      setLoading(false);
      // delay so it doesnt flicker as it closes
      setTimeout(() => {
        setEmail('');
        setPassword('');
        setEmailError(' ');
        setPasswordError(' ');
        setResettingPassword(false);
      }, 100);
    });

  };

  const onEmailChange = (event: any) => {
    setEmail(event.target.value);
    setEmailError(' ');
  };

  const onPasswordChange = (event: any) => {
    setPassword(event.target.value);
    setPasswordError(' ');
  };

  const validateInputs = () => {
    let isValid = true;

    if (email.trim() === '') {
      setEmailError('Field cannot be left blank.');
      isValid = false;
    }

    if (email.trim().length > 50) {
      setEmailError('Email cannot be longer than 50 characters.');
      isValid = false;
    }

    if (password.trim() === '') {
      setPasswordError('Field cannot be left blank.');
      isValid = false;
    }

    if (creatingAccount) {
      if (password.trim().length > 0 && password.length < 8) {
        setPasswordError('Password must be at least 8 characters.');
        isValid = false;
      }
    }

    return isValid;
  };

  const handleLogin = async () => {
    if (!validateInputs()) {
      return;
    }

    // if we got here from handleSignUp, loading is already true
    if (!loading) setLoading(true);

    try {
      await signInWithEmailAndPassword(auth, email, password);
      closeDialog();
    } catch (error: any) {
      setLoading(false);

      if (error.code === ServerErrors.INVALID_PASSWORD) {
        setPasswordError('Invalid password.');
        return;
      }

      if (error.code === ServerErrors.USER_NOT_FOUND) {
        setEmailError('Email not found.');
        return;
      }

      console.log('login error:');
      console.log(error);

      // generic catch-all
      setEmailError('Something went wrong. Try again.');
      return;
    }
  };

  const handleSignUp = async () => {
    if (!validateInputs()) {
      return;
    }
  
    setLoading(true);

    try {
      const puzzleStates = loadAllPuzzleStates();
      await axios.post('/api/users/create-user', { email, password, puzzleStates });
    } catch (error) {
      setLoading(false);
      const errorCode = ((error as AxiosError).response as any).data.error;

      if (errorCode === ServerErrors.EMAIL_ALREADY_IN_USE) {
        setEmailError('There is already an account for this email.');
        return;
      }

      if (errorCode === ServerErrors.INVALID_EMAIL) {
        setEmailError('Invalid email. Check formatting?');
        return;
      }

      if (errorCode === ServerErrors.WEAK_PASSWORD) {
        setPasswordError('Password must be at least 8 characters.');
        return;
      }

      console.log('signup error:');
      console.log(error);

      // generic catch-all
      setEmailError('Something went wrong.');
      return;
    }

    handleLogin();
  };

  const handlePasswordReset = async () => {
    if (email.trim() === '') {
      setEmailError('This field cannot be blank.');
      return;
    }

    setLoading(true);

    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error: any) {
      setLoading(false);

      if (error.code === ServerErrors.INVALID_EMAIL) {
        setEmailError('Invalid email.');
        return;
      }

      if (error.code === ServerErrors.USER_NOT_FOUND) {
        setEmailError('Email not found.');
        return;
      }
    }

    setLoading(false);
    closeDialog();
    alert('A password reset email has been sent.');
  };

  const executeMainAction = () => {
    if (creatingAccount) {
      handleSignUp();
    } else if (resettingPassword) {
      handlePasswordReset();
    } else {
      handleLogin();
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={closeDialog}
      aria-labelledby="form-dialog-title"
      PaperProps={{
        className: 'signInDialogContainer',
      }}
    >
      <DialogTitle className="signInDialogTitle">
        {getMainButtonText() /*cuz it's the same as the button*/}
      </DialogTitle>
      <DialogContent>
        <form className="signUpForm">
          <TextField
            error={!!emailError.trim()}
            helperText={emailError}
            id="email"
            autoComplete="email"
            className="emailField"
            label="Email"
            variant="outlined"
            margin="dense"
            value={email}
            onChange={onEmailChange}
            onFocus={() => { setEmailError(' '); }}
            fullWidth
          />

          {!resettingPassword &&
          <TextField
            error={!!passwordError.trim()}
            helperText={passwordError}
            id="password"
            className="passwordField"
            label="Password"
            type="password"
            variant="outlined"
            autoComplete="current-password"
            margin="dense"
            value={password}
            onChange={onPasswordChange}
            onKeyDown={(event: React.KeyboardEvent) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                creatingAccount ? handleSignUp() : handleLogin();
              }
            }}
            onFocus={() => { setPasswordError(' '); }}
            fullWidth
          />}

          <Button
            className="signUpButton"
            color="primary"
            variant="contained"
            onClick={executeMainAction}
          >
            {getMainButtonText()}
          </Button>

          <Button
            color="primary"
            className="forgotPassword"
            onClick={() => {
              if (!resettingPassword) {
                setCreatingAccount(false);
              }
              setResettingPassword(!resettingPassword);
            }}
          >
            {resettingPassword ? 'Go back' : 'Forgot your password?'}
          </Button>

          <span className="loginOrSignupSwitchLabel">
            {creatingAccount ?
              'Already have an account?'
              : 'New to the Daily Baffle?'}
          </span>

          <Button
            color="primary"
            className="loginOrSignUpSwitch"
            onClick={() => {
              setEmail('');
              setPassword('');
              setCreatingAccount(!creatingAccount);
              setResettingPassword(false);
            }}
          >
            {creatingAccount ? 'Log In' : 'Sign Up'}
          </Button>

        </form>

        {loading &&
          <Backdrop className="loadingBackdrop" open={loading}>
            <BarLoader color="#36d7b7"/>
          </Backdrop>
        }
      </DialogContent>
    </Dialog>
  );
};

export default SignInDialog;